import { PayloadAction } from '@reduxjs/toolkit';
import { QueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { put, takeLatest, select } from 'redux-saga/effects';
import { ProjectId, TaskId } from 'api/models';
import { TaskAPI, Task } from 'api/tasks';
import { FetchStatus } from 'data/core';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import {
  removeAsSubtaskSagaAction,
  FetchTaskActionPayload,
  removeAsSubtaskSuccessAction,
} from '../actions';
import { selectTask } from '../selectors';
import { deleteTask, saveTasks } from '../slice';

interface RemoveAsSubtaskPayload extends FetchTaskActionPayload {
  queryClient: QueryClient;
  parentTaskId: number | undefined;
  phaseId?: number;
}

function* removeAsSubtaskSaga({
  payload,
}: PayloadAction<RemoveAsSubtaskPayload>): Generator {
  const { projectId, taskId, queryClient, parentTaskId, phaseId } = payload;
  const task = (yield select(selectTask(TaskId(projectId, taskId)))) as Task;
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );

  if (!queryTasks) {
    return;
  }

  try {
    const updatedTask = (yield TaskAPI.removeAsSubtask(taskId)) as Task;

    yield put(deleteTask(TaskId(projectId, taskId)));

    yield put(
      saveTasks({ data: [updatedTask], fetchStatus: FetchStatus.Loaded }),
    );

    if (parentTaskId) {
      yield invalidateTaskQuery(parentTaskId);
    }

    const updatedTasks = queryTasks.map((t) => {
      if (t.taskId === taskId) {
        return updatedTask;
      }
      if (t.taskId === parentTaskId && t.subTasks && t.subTaskOrder) {
        return {
          ...t,
          subTasks: t.subTasks.filter((subtask) => subtask.taskId !== taskId),
          subTaskOrder: t.subTaskOrder.filter(
            (subtaskId) => subtaskId !== taskId,
          ),
        };
      }

      return t;
    });

    const queryKey = getProjectTasksQueryKey(ProjectId(projectId));

    queryClient.setQueryData<Task[]>(queryKey, updatedTasks);

    yield queryClient.invalidateQueries(
      getProjectDetailQueryKey(ProjectId(projectId)),
    );

    yield queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));

    if (!phaseId) {
      return;
    }

    yield put(
      removeAsSubtaskSuccessAction({
        taskId,
        projectId,
        phaseId,
      }),
    );
  } catch (error) {
    message.error(
      'There was an error processing your request.',
      error as unknown as any,
    );
    if (task) {
      yield put(saveTasks({ data: [task], fetchStatus: FetchStatus.Loaded }));
    }
  }
}

export const watchRemoveAsSubtaskSaga = function* (): Generator {
  yield takeLatest(removeAsSubtaskSagaAction.type, removeAsSubtaskSaga);
};
